import { useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAgentOverrider } from '@atlassian/conversation-assistant-agent/controllers/agent-overrider';
import { useAgentsPaginatedByAgentId } from '@atlassian/conversation-assistant-agent/controllers/agents';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { ASSISTANCE_SERVICE_ERROR_CODES } from '@atlassian/conversation-assistant-service-api/constants';
import {
	type Agent,
	isChannelNotFoundError,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	type AgentVersion,
	CHAT_STORE_STATUS,
	type Conversation,
	type StoreAssistant,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessages } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';
import { removeTasksForConversation } from '@atlassian/conversation-assistant-store/controllers/tasks';
import { useTasksStoreActions as useUnifiedTasksStoreActions } from '@atlassian/conversation-assistant-store/controllers/unified-tasks';

import { useAssistanceServiceParams } from '../../common/utils/use-assistance-service-params';
import { useAgentVersioningConfig } from '../agent-versioning-config';
import { useChatStore, useChatStoreActions } from '../chat-store/main';
import { getFirstMessageOfType, isEmptyConversation } from '../chat-store/utils';

type UseConversationProps = {
	conversationId?: string;
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
};

/**
 * Checks if an error code indicates a fallback agent scenario (forbidden or deactivated agent).
 * This is used to determine when to create fallback agents or when to prevent using agents for new chats.
 */
const isAgentUnavailable = (errorCode?: string): boolean => {
	return (
		errorCode === ASSISTANCE_SERVICE_ERROR_CODES.AGENT_FORBIDDEN ||
		errorCode === ASSISTANCE_SERVICE_ERROR_CODES.AGENT_DEACTIVATED
	);
};

export const useConversation = ({
	conversationId: conversationIdParam,
	assistanceServiceParams,
}: UseConversationProps = {}): {
	conversation?: Conversation;
	selectedAgent?: Agent;
	isFetching?: boolean;
	agentErrorCode?: string;
	agentIdForNewChat?: string;
	agentVersion?: AgentVersion;
	agentVersionForNewChat?: AgentVersion;
} => {
	const [{ currentConversationId, conversations }] = useChatStore();
	const fallbackParams = useAssistanceServiceParams({
		touchpointSource: 'useConversationHook',
	});
	const serviceParams = assistanceServiceParams || fallbackParams;
	const conversationId = conversationIdParam ?? currentConversationId;
	const conversation = conversations[conversationId] as Conversation | undefined;
	const [messages] = useChatMessages({
		conversationId,
	});

	const { agentDefaultIdOverride } = useAgentOverrider();

	const agentId = useMemo(() => {
		const agentIdFromConversation =
			conversation?.sourceAgentId ??
			getFirstMessageOfType<StoreAssistant>(messages ?? [], 'ASSISTANT')?.content.message.message
				.author_id;

		if (fg('rovo_chat_fix_agent_selection_after_cancel')) {
			// fixing the fallback to correctly select an agent if a user cancels a session
			return agentIdFromConversation ?? conversation?.agent?.agent_id ?? agentDefaultIdOverride;
		}

		return agentIdFromConversation ?? agentDefaultIdOverride;
	}, [
		agentDefaultIdOverride,
		conversation?.sourceAgentId,
		conversation?.agent?.agent_id,
		messages,
	]);

	const { agent, error: agentError } = useAgentsPaginatedByAgentId({
		agentId: agentId,
		assistanceServiceParams: serviceParams,
	});

	const agentErrorCode = agentError?.errorAttributes?.errorCode;

	/**
	 * if agent fetch failed with status code AGENT_FORBIDDEN or AGENT_DEACTIVATED, extract agent name from messages because user has no access to the agent endpoint anymore
	 * scenario: user has access to an agent, created a conversation with it, but then lost access to the agent, this will be triggered when user tries to reopen the conversation;
	 * scenario: user has access to an agent, created a conversation with it, but then the agent was deactivated, this will be triggered when user tries to reopen the conversation;
	 */
	const fallbackAgent = useMemo(() => {
		if (!fg('convo-ai-enable-agents-use-permission')) {
			return undefined;
		}

		// Don't create fallback agent for empty conversations (new chats)
		if (isEmptyConversation(conversationId) || !agentId) {
			return undefined;
		}

		// Only create fallback agent when agent fetch failed (to extract agent name from messages for display)
		// If agent was successfully fetched, no need for fallback
		if (!isAgentUnavailable(agentErrorCode)) {
			return undefined;
		}

		// Try to extract agent metadata from the first assistant message
		const firstAssistantMessage = getFirstMessageOfType<StoreAssistant>(
			messages ?? [],
			'ASSISTANT',
		);
		const author = firstAssistantMessage?.content.message.message.author;

		if (!author?.name) {
			return undefined;
		}

		// Create a minimal Agent object with only required fields from message metadata
		// This allows the UI to display the agent name even when access is denied
		return {
			id: agentId,
			named_id: author.named_id ?? agentId,
			name: author.name,
			description: null,
			creator_type: 'CUSTOMER' as const,
			available_plugins: {},
			is_default: false,
			actor_type: 'AGENT' as const,
			user_defined_conversation_starters: null,
			favourite: false,
			deactivated: false,
		} satisfies Agent;
	}, [agentId, messages, agentErrorCode, conversationId]);

	const selectedAgent = agent ?? fallbackAgent;

	// Calculate agentIdForNewChat: only use valid agents (not fallback/error state) for new chats
	const agentIdForNewChat = useMemo(() => {
		if (!fg('convo-ai-enable-agents-use-permission')) {
			return selectedAgent?.id;
		}

		// No agent selected
		if (!selectedAgent?.id) {
			return undefined;
		}

		// If agent fetch failed with forbidden/deactivated error, don't use it for new chats
		if (isAgentUnavailable(agentErrorCode)) {
			return undefined;
		}

		// If conversation has an error state, the agent is a fallback - don't use it for new chats
		if (conversation?.status === CHAT_STORE_STATUS.deactivatedAgentError) {
			return undefined;
		}

		// Valid agent - safe to use for new chats
		return selectedAgent?.id;
	}, [agentErrorCode, selectedAgent?.id, conversation?.status]);

	const agentVersioningConfig = useAgentVersioningConfig();

	const agentVersion = useMemo(() => {
		if (!fg('rovo_agent_versioning_enabled')) {
			return undefined;
		}

		// Prefer version stored on conversation (from chat-new event)
		if (conversation?.agentVersion) {
			return conversation.agentVersion;
		}

		// Fall back to version from first assistant message author (from BE response)
		const firstAssistantMessage = getFirstMessageOfType<StoreAssistant>(
			messages ?? [],
			'ASSISTANT',
		);
		const author = firstAssistantMessage?.content.message.message.author;

		if (author?.versionId || author?.versionType) {
			return {
				versionId: author.versionId,
				versionType: author.versionType,
				versionNumber: author.versionNumber,
			};
		}

		return undefined;
	}, [conversation?.agentVersion, messages]);

	const agentVersionForNewChat = useMemo(() => {
		if (!agentVersioningConfig?.preserveVersionOnNewChat) {
			return undefined;
		}
		return agentVersion;
	}, [agentVersioningConfig?.preserveVersionOnNewChat, agentVersion]);

	return {
		conversation,
		selectedAgent,
		agentErrorCode,
		agentIdForNewChat,
		agentVersion,
		agentVersionForNewChat,
	};
};

/**
 * TODO: Deprecated; this is only used in navigation hook as a stop gap solution,
 * we need to use the centralized store actions instead, do not use this for any other cases
 *
 * Conversation rename and delete actions that are usable outside of ChatStoreContainer
 * via createHookWithAssistanceService
 */
export const useConversationActions: (
	props: Omit<AssistanceServiceInjectedParams, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => {
	deleteConversation: ({ conversationId }: { conversationId: string }) => Promise<void>;
	updateConversation: ({
		conversationId,
		data,
	}: {
		conversationId: string;
		data: Pick<Conversation, 'name'>;
	}) => Promise<void>;
} = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams: { assistanceService, experienceTracker },
	}: AssistanceServiceInjectedParams) => {
		const [{ conversations, currentConversationId }] = useChatStore();
		const { removeConversationFromStore, setConversationData, setSelectedConversation } =
			useChatStoreActions();
		const { removeTasksForConversation: unifiedRemoveTasksForConversation } =
			useUnifiedTasksStoreActions();

		const deleteConversationWithAssistanceService = async ({
			conversationId,
		}: {
			conversationId: string;
		}) => {
			experienceTracker.start({
				name: ExperienceName.CLEAR_CHAT,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			conversations?.[conversationId]?.storeAbortController?.abort();

			try {
				await assistanceService.deleteChat(conversationId);
			} catch (error: any) {
				if (isChannelNotFoundError(error)) {
					removeConversationFromStore({ conversationId });

					if (currentConversationId === conversationId) {
						setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
					}
				}

				experienceTracker.fail({
					name: ExperienceName.CLEAR_CHAT,
					error,
				});

				throw error;
			}

			removeConversationFromStore({ conversationId });

			if (fg('rovo_chat_unified_tasks_store')) {
				unifiedRemoveTasksForConversation(conversationId);
			} else {
				removeTasksForConversation(conversationId);
			}

			if (currentConversationId === conversationId) {
				// reset the selected conversation to the empty conversation id
				// this is stop gap solution until we start to use the new conversation store actions,
				// without this the user will be stuck on the deleted conversation as that is the last selected conversation
				setSelectedConversation({ conversationId: EMPTY_CONVERSATION_ID });
			}

			experienceTracker.succeed({
				name: ExperienceName.CLEAR_CHAT,
			});
		};

		const updateConversationWithAssistanceService = async ({
			conversationId,
			data,
		}: {
			conversationId: string;
			data: Pick<Conversation, 'name'>;
		}) => {
			experienceTracker.start({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
				id: conversationId,
				attributes: {
					conversationId,
				},
			});

			try {
				await assistanceService.updateConversationChannel(conversationId, {
					name: data.name,
				});
			} catch (error: any) {
				experienceTracker.fail({
					name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
					error,
				});

				throw error;
			}

			setConversationData({
				conversationId,
				data,
			});

			experienceTracker.succeed({
				name: ExperienceName.UPDATE_CONVERSATION_CHANNEL,
			});
		};

		return {
			deleteConversation: deleteConversationWithAssistanceService,
			updateConversation: updateConversationWithAssistanceService,
		};
	},
);

/**
 * Syncs agent error state (AGENT_FORBIDDEN or AGENT_DEACTIVATED) to conversation status.
 * This should be called once at the top level (e.g., main.tsx) to ensure conversation
 * state reflects agent access errors for existing conversations only.
 */
export const useSyncConversationStatusOnAgentError = ({
	agentErrorCode,
	conversationId,
}: {
	agentErrorCode?: string;
	conversationId?: string;
}): void => {
	const { setConversationData } = useChatStoreActions();

	useEffect(() => {
		// Don't set error state for empty conversations (new chats)
		if (!conversationId || isEmptyConversation(conversationId)) {
			return;
		}

		if (
			agentErrorCode &&
			isAgentUnavailable(agentErrorCode) &&
			fg('convo-ai-enable-agents-use-permission')
		) {
			setConversationData({
				conversationId,
				data: {
					status: CHAT_STORE_STATUS.deactivatedAgentError,
					errorCode: agentErrorCode,
				},
			});
		}
	}, [agentErrorCode, conversationId, setConversationData]);
};
